import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Shell, type Tela } from './components/Shell'
import { Calculadora } from './pages/Calculadora'
import { Dashboard } from './pages/Dashboard'
import { DocumentosRecebidos } from './pages/DocumentosRecebidos'
import { Empresa } from './pages/Empresa'
import { Foco2027 } from './pages/Foco2027'
import { Panorama } from './pages/Panorama'
import { PlanejamentoRegime } from './pages/PlanejamentoRegime'
import { ReceitasSemNota } from './pages/ReceitasSemNota'
import { Relatorios } from './pages/Relatorios'
import { SimplesHibrido } from './pages/SimplesHibrido'
import { EnviarDocumentos } from './pages/EnviarDocumentos'
import { HabilitarEmpresas } from './pages/HabilitarEmpresas'
import { Integracoes } from './pages/Integracoes'
import { Login } from './pages/Login'
import { QualidadeBase } from './pages/QualidadeBase'
import { CreditoEmRisco, Fornecedores, MixDestinacao } from './pages/Contrapartes'
import { Pendencias } from './pages/Pendencias'
import { RevisaoEstoque } from './pages/RevisaoEstoque'
import { TotpInscrever, TotpVerificar } from './pages/Totp'
import { useTRPC } from './trpc'

const TELAS: Tela[] = ['dashboard', 'empresa', 'habilitar', 'integracoes', 'enviar', 'documentos', 'qualidade', 'produtos', 'insumos', 'fornecedores', 'clientes', 'credito', 'pendencias', 'foco2027', 'simples', 'panorama', 'relatorios', 'regimes', 'calculadora', 'receitas']
const lerHash = (): Tela => {
  const h = window.location.hash.replace('#/', '') as Tela
  return TELAS.includes(h) ? h : 'dashboard'
}

/**
 * Máquina de estados da entrada: sem sessão → login → 2º fator → app.
 * Quem decide o passo é o servidor (`sessao.contexto`), não o front.
 */
export function App() {
  const trpc = useTRPC()
  const contexto = useQuery(trpc.sessao.contexto.queryOptions())
  const [tela, setTela] = useState<Tela>(lerHash)

  useEffect(() => {
    const aoMudar = () => setTela(lerHash())
    window.addEventListener('hashchange', aoMudar)
    return () => window.removeEventListener('hashchange', aoMudar)
  }, [])

  const ir = (t: Tela) => {
    window.location.hash = `/${t}`
    setTela(t)
  }

  if (contexto.isPending) {
    return <div className="grid h-full place-items-center text-sm text-tinta-3">Carregando…</div>
  }
  const c = contexto.data
  if (!c) return <Login />
  if (c.proximoPasso === 'inscrever_totp') return <TotpInscrever />
  if (c.proximoPasso === 'totp') return <TotpVerificar />

  // Telas da empresa sem empresa selecionada caem no dashboard, que ensina a selecionar.
  const precisaEmpresa: Tela[] = ['empresa', 'enviar', 'documentos', 'qualidade', 'produtos', 'insumos', 'fornecedores', 'clientes', 'credito', 'pendencias', 'foco2027', 'simples', 'panorama', 'relatorios', 'regimes', 'calculadora', 'receitas']
  const atual = !c.empresaAtiva && precisaEmpresa.includes(tela) ? 'dashboard' : tela

  return (
    <Shell contexto={c} tela={atual} ir={ir}>
      {/* key: trocar de empresa remonta a tela — nada de estado da empresa anterior. */}
      <div key={c.empresaAtiva?.id ?? 'sem-empresa'}>
        {atual === 'dashboard' && <Dashboard contexto={c} ir={ir} />}
        {atual === 'empresa' && <Empresa contexto={c} />}
        {atual === 'habilitar' && <HabilitarEmpresas />}
        {atual === 'integracoes' && <Integracoes />}
        {atual === 'enviar' && <EnviarDocumentos contexto={c} />}
        {atual === 'documentos' && <DocumentosRecebidos contexto={c} irParaEnvio={() => ir('enviar')} />}
        {atual === 'qualidade' && <QualidadeBase irParaEnvio={() => ir('enviar')} />}
        {atual === 'produtos' && <RevisaoEstoque key="SAIDA" tipo="SAIDA" contexto={c} />}
        {atual === 'insumos' && <RevisaoEstoque key="ENTRADA" tipo="ENTRADA" contexto={c} />}
        {atual === 'fornecedores' && <Fornecedores contexto={c} irPendencias={() => ir('pendencias')} />}
        {atual === 'clientes' && <MixDestinacao contexto={c} irPendencias={() => ir('pendencias')} />}
        {atual === 'credito' && <CreditoEmRisco irFornecedores={() => ir('fornecedores')} />}
        {atual === 'pendencias' && <Pendencias contexto={c} />}
        {atual === 'foco2027' && <Foco2027 contexto={c} ir={ir} />}
        {atual === 'simples' && <SimplesHibrido contexto={c} ir={ir} />}
        {atual === 'panorama' && <Panorama contexto={c} ir={ir} />}
        {atual === 'relatorios' && <Relatorios contexto={c} ir={ir} />}
        {atual === 'calculadora' && <Calculadora />}
        {atual === 'receitas' && <ReceitasSemNota contexto={c} />}
        {atual === 'regimes' && <PlanejamentoRegime contexto={c} />}
      </div>
    </Shell>
  )
}
